import { useRef, useCallback } from "react";
import slotsConfig from "./SlotsConfig_Summer2026";
import { TENT_SUBDIVISIONS, getOrientedLayoutStyle } from "./Tentvisuals";
import { useMapInteraction } from "./Hooks/useMapInteraction";
import "./VendorMap.css";

// ── Grid constants ─────────────────────────────────────────────────────────────
const CELL = 22;
// PAD is the empty "grass" buffer drawn around the real vendor grid, in
// the same units as CELL. Bumped up from 16 so that on wide/tall screens,
// the "slice" crop (see VendorMap.jsx's preserveAspectRatio) eats into
// this empty buffer instead of cropping actual vendor tents at the edges.
const PAD  = 140;

// ── Slot type config ───────────────────────────────────────────────────────────
export const TENT_COLORS = {
  "17x17-pod":    "#ffaa6e",
  "20x20-double": "#74bfed",
  "20x20-single": "#bc8fe8",
  "20x20":        "#d0c9f5",
  "10x10-single": "#ff8080",
  "Food Court":   "#5ee8a8",
  "20x20_open":   "#64cff5",
  sponsored:      "#ffe44d",
  reserved:       "#f590b0",
  space:          "#feca62",
  "space-3":      "#f6b23e",
};

export const TENT_LABELS = {
  "17x17-pod":    "17×17 Pod",
  "20x20-double": "20×20 Sharing",
  "20x20-single": "20×20 Private",
  "10x10-single": "10×10 Single",
  "20x20":        "20×20",
  "Food Court":   "Food Court",
  reserved:       "Reserved",
  space:          "Space",
  "space-3":      "Space (3)",
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function parseGrid(val) {
  if (!val) return { start: 1, span: 1 };
  const str = String(val).trim();
  const m = str.match(/(\d+)\s*\/?\s*span\s*(\d+)/i);
  if (m) return { start: parseInt(m[1]), span: parseInt(m[2]) };
  const n = str.match(/(\d+)/);
  return { start: n ? parseInt(n[1]) : 1, span: 1 };
}

function slotRect(slot) {
  const c = parseGrid(slot.gridColumn);
  const r = parseGrid(slot.gridRow);
  return {
    x: (c.start - 1) * CELL,
    y: (r.start - 1) * CELL,
    w: c.span * CELL,
    h: r.span * CELL,
  };
}

function hexLighten(hex, f) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (
    "#" +
    [r, g, b]
      .map(v =>
        Math.min(255, Math.round(v + (255 - v) * f))
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
}

// Resolve which tent-mode a 20x20 tile should render as. Mirrors admin's
// effectiveType memo in SlotGrid.jsx: prefer an explicit tent_mode stored on
// any booking for this tile, and if none is present, infer single vs double
// from which sub_ids actually exist. Only truly empty tiles (no bookings at
// all) fall back to the generic, non-subdivided "20x20".
export function resolveTentMode(slot, bookings) {
  if (slot.type !== "20x20") return slot.type;

  const withMode = bookings.find(b => b.tent_mode);
  if (withMode) return withMode.tent_mode;

  if (bookings.length > 0) {
    const subsPresent = new Set(bookings.map(b => b.sub_id || "A"));
    return subsPresent.size > 1 || subsPresent.has("B")
      ? "20x20-double"
      : "20x20-single";
  }

  return "20x20";
}

// Partition a tile's inner rect into sub-slot cells for a given layout code —
// the SVG equivalent of the admin's g22/g12/g31 CSS grid classes.
//
// Orientation now follows the tile's `facing` (which edge is open to the
// walkway), the same rule as Tentvisuals.js's getOrientedLayoutStyle: a
// left/right-facing tile stacks its subs top/bottom so each one keeps its
// own full-width frontage on the open edge; a top/down-facing tile (or one
// with no facing set) splits side by side. This replaces the previous
// width-vs-height guess, which could pick the wrong axis and make it
// impossible to tell which side of a shared tent a vendor is actually on.
function subRectsForLayout(x, y, w, h, layout, subs, facing) {
  const stacked = facing === "left" || facing === "right";

  switch (layout) {
    case "g22": {
      // Symmetric on all four sides — never needs reorientation.
      const hw = w / 2, hh = h / 2;
      return [
        { sub: subs[0], rx: x,      ry: y,      rw: hw, rh: hh },
        { sub: subs[1], rx: x + hw, ry: y,      rw: hw, rh: hh },
        { sub: subs[2], rx: x,      ry: y + hh, rw: hw, rh: hh },
        { sub: subs[3], rx: x + hw, ry: y + hh, rw: hw, rh: hh },
      ];
    }
    case "g12": {
      if (stacked) {
        const hh = h / 2;
        return [
          { sub: subs[0], rx: x, ry: y,      rw: w, rh: hh },
          { sub: subs[1], rx: x, ry: y + hh, rw: w, rh: hh },
        ];
      }
      const hw = w / 2;
      return [
        { sub: subs[0], rx: x,      ry: y, rw: hw, rh: h },
        { sub: subs[1], rx: x + hw, ry: y, rw: hw, rh: h },
      ];
    }
    case "g31": {
      // Not covered by Tentvisuals' getOrientedLayoutStyle (g11/g12/g22
      // only) — extended here with the same left/right → stacked rule for
      // consistency with the rest of the tile shapes.
      if (stacked) {
        const th = h / 3;
        return subs.map((sub, i) => ({ sub, rx: x, ry: y + i * th, rw: w, rh: th }));
      }
      const tw = w / 3;
      return subs.map((sub, i) => ({ sub, rx: x + i * tw, ry: y, rw: tw, rh: h }));
    }
    default:
      return [{ sub: subs[0] || "A", rx: x, ry: y, rw: w, rh: h }];
  }
}

// ── Compute tight viewBox once ─────────────────────────────────────────────────
const { VB_X, VB_Y, VB_W, VB_H } = (() => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  slotsConfig.forEach(s => {
    const r = slotRect(s);
    minX = Math.min(minX, r.x);     minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w); maxY = Math.max(maxY, r.y + r.h);
  });
  return {
    VB_X: minX - PAD,
    VB_Y: minY - PAD,
    VB_W: maxX - minX + PAD * 2,
    VB_H: maxY - minY + PAD * 2,
  };
})();

// ── SVG Defs ───────────────────────────────────────────────────────────────────
const MapDefs = () => (
  <defs>
    <pattern id="pat-grass" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
      <rect width="24" height="24" fill="#c5dba0"/>
      <circle cx="4"  cy="8"  r="1.2" fill="#b4ca8e" opacity="0.6"/>
      <circle cx="14" cy="4"  r="1.0" fill="#aabe84" opacity="0.5"/>
      <circle cx="19" cy="16" r="1.3" fill="#b4ca8e" opacity="0.55"/>
      <circle cx="8"  cy="18" r="0.9" fill="#aabe84" opacity="0.5"/>
      <circle cx="21" cy="7"  r="0.7" fill="#bfcf98" opacity="0.45"/>
    </pattern>

    <pattern id="tex-20x20" x="0" y="0" width="9" height="9" patternUnits="userSpaceOnUse">
      <path d="M0 9L9 0M-2 2L2-2M7 11L11 7" stroke="rgba(255,255,255,0.18)" strokeWidth="0.7"/>
    </pattern>
    <pattern id="tex-pod" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
      <circle cx="3" cy="3" r="0.9" fill="rgba(255,255,255,0.22)"/>
    </pattern>
    <pattern id="tex-10x10" x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse">
      <path d="M0 5L5 0" stroke="rgba(255,255,255,0.26)" strokeWidth="0.65"/>
    </pattern>
    <pattern id="tex-food" x="0" y="0" width="10" height="6" patternUnits="userSpaceOnUse">
      <path d="M0 4 Q2.5 1 5 4 Q7.5 7 10 4" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.7"/>
    </pattern>

    <filter id="f-shadow" x="-12%" y="-12%" width="134%" height="148%">
      <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" floodColor="rgba(0,0,0,0.14)"/>
    </filter>
    <filter id="f-glow" x="-25%" y="-25%" width="155%" height="165%">
      <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#7F77DD" floodOpacity="0.7"/>
    </filter>
    <filter id="f-occupied" x="-12%" y="-12%" width="134%" height="148%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="rgba(0,0,0,0.22)"/>
    </filter>
  </defs>
);

const texMap = {
  "20x20":        "url(#tex-20x20)",
  "20x20-single": "url(#tex-20x20)",
  "20x20-double": "url(#tex-20x20)",
  "17x17-pod":    "url(#tex-pod)",
  "10x10-single": "url(#tex-10x10)",
  "Food Court":   "url(#tex-food)",
};

// ── SvgPathway ─────────────────────────────────────────────────────────────────
function SvgPathway({ slot }) {
  const { x, y, w, h } = slotRect(slot);
  const label      = slot.label?.trim() || "";
  const isVertical = h > w;
  const cx         = x + w / 2;
  const cy         = y + h / 2;

  const charW  = 5.5;
  const pillW  = Math.min(label.length * charW + 22, isVertical ? h - 8 : w - 8);
  const pillH  = 14;
  const pillR  = pillH / 2;
  const pillX  = cx - pillW / 2;
  const pillY  = cy - pillH / 2;

  return (
    <g aria-label={`Walkway: ${label}`}>
      {isVertical ? (
        <line
          x1={cx} y1={y + 2} x2={cx} y2={y + h - 2}
          stroke="rgba(160,130,80,0.55)" strokeWidth={1.5}
          strokeDasharray="8 6" strokeLinecap="round"
        />
      ) : (
        <line
          x1={x + 2} y1={cy} x2={x + w - 2} y2={cy}
          stroke="rgba(160,130,80,0.55)" strokeWidth={1.5}
          strokeDasharray="8 6" strokeLinecap="round"
        />
      )}

      {(isVertical ? h >= 18 : w >= 24) && label && (
        <g transform={isVertical ? `rotate(-90, ${cx}, ${cy})` : ""}>
          <rect
            x={pillX + 0.5} y={pillY + 1} width={pillW} height={pillH}
            rx={pillR} ry={pillR} fill="rgba(0,0,0,0.08)"
          />
          <rect
            x={pillX} y={pillY} width={pillW} height={pillH}
            rx={pillR} ry={pillR}
            fill="rgba(255,255,255,0.88)"
            stroke="rgba(160,135,90,0.4)" strokeWidth={0.8}
          />
          <rect
            x={pillX + 5} y={pillY + 4} width={7} height={5}
            rx={1} fill="none" stroke="#b07d3a" strokeWidth={0.9}
          />
          <line
            x1={pillX + 8.5} y1={pillY + 3}
            x2={pillX + 8.5} y2={pillY + 2}
            stroke="#b07d3a" strokeWidth={0.9} strokeLinecap="round"
          />
          <text
            x={pillX + 16} y={pillY + pillH / 2 + 3.5}
            fontSize={6.5} fontWeight="700"
            fontFamily="'DM Sans', 'Inter', system-ui, sans-serif"
            letterSpacing="1.4" fill="#6b4f1e" textAnchor="start"
          >
            {label.toUpperCase()}
          </text>
        </g>
      )}
    </g>
  );
}

// ── MapTile ────────────────────────────────────────────────────────────────────
function MapTile({ slot, bookings, isReserved, isActive, onClick }) {
  const { x, y, w, h } = slotRect(slot);
  const booking     = bookings?.[0] || null;
  const vendorCount = bookings?.length || 0;

  // Entry / exit blockers
  if (slot.type === "blocker") {
    const isEntry = slot.id === "Entry";
    const isExit  = slot.id === "Exit";
    if (!isEntry && !isExit) return null;
    const fill   = isEntry ? "#bbf7d0" : "#fee2e2";
    const stroke = isEntry ? "#4ade80" : "#f87171";
    const tc     = isEntry ? "#166534" : "#991b1b";
    const rw     = Math.max(w, CELL * 2);
    const rh     = Math.max(h, CELL);
    return (
      <g>
        <rect x={x} y={y} width={rw} height={rh} rx={6}
          fill={fill} stroke={stroke} strokeWidth={1.5}
          filter="url(#f-shadow)"/>
        <rect x={x+2} y={y+2} width={rw-4} height={2.5}
          rx={1.5} fill="rgba(255,255,255,0.45)"/>
        <text x={x+rw/2} y={y+rh/2+4} textAnchor="middle"
          fill={tc} fontSize={8} fontWeight="800"
          fontFamily="sans-serif" letterSpacing="1">
          {isEntry ? "▶ ENTRY" : "EXIT ◀"}
        </text>
      </g>
    );
  }

  // Regular vendor tiles
  const effectiveType = isReserved ? "reserved" : resolveTentMode(slot, bookings);

  const baseColor    = TENT_COLORS[effectiveType] || TENT_COLORS["20x20"];
  const tex          = texMap[effectiveType] || texMap[slot.type];
  const hasVendor    = vendorCount > 0 && !isReserved;
  const rx           = slot.type === "10x10-single" ? 3 : 5;
  const IN           = 1.5;
  const fs           = w < CELL * 1.5 ? 6 : w < CELL * 2 ? 7 : 8;
  const displayColor = hasVendor || isReserved ? baseColor : hexLighten(baseColor, 0.35);
  const filterId     = isActive
    ? "url(#f-glow)"
    : hasVendor
      ? "url(#f-occupied)"
      : "url(#f-shadow)";

  // ── Multi-sub tiles (pods, shared 20×20s, the 3-way podium/D24) ──────────────
  // Show each sub-slot individually instead of one logo + a "+N" badge, so a
  // vendor can see at a glance which specific spot inside the tile is theirs.
  // Clicking anywhere on a multi-sub tile still opens the shared onClick
  // (the slot-level floor-plan modal handles picking a specific vendor from
  // there) — see VendorMapPage's handleTileClick / SlotVendorsModal.
  const subDef    = TENT_SUBDIVISIONS[effectiveType];
  const isMultiSub = !!subDef && subDef.subs.length > 1;

  if (isMultiSub) {
    const cells = subRectsForLayout(x + IN, y + IN, w - IN * 2, h - IN * 2, subDef.layout, subDef.subs, slot.facing);
    return (
      <g filter={filterId} aria-label={`${slot.label}: ${vendorCount} of ${subDef.subs.length} filled`}>
        <rect
          x={x+IN} y={y+IN} width={w-IN*2} height={h-IN*2} rx={rx}
          fill={hexLighten(baseColor, 0.55)}
          stroke={isActive ? "#7F77DD" : "rgba(0,0,0,0.16)"}
          strokeWidth={isActive ? 2.2 : 0.8}
          onClick={onClick}
          style={{ cursor: "pointer" }}
        />
        {cells.map(cell => {
          const b = bookings.find(bk => (bk.sub_id || "A") === cell.sub);
          const filled = !!b;
          const cw = cell.rw - 1.6, ch = cell.rh - 1.6;
          return (
            <g key={cell.sub} onClick={onClick} style={{ cursor: "pointer" }}>
              <rect
                x={cell.rx + 0.8} y={cell.ry + 0.8} width={cw} height={ch}
                rx={Math.min(3, cw / 4, ch / 4)}
                fill={filled ? baseColor : "rgba(255,255,255,0.5)"}
                stroke="rgba(0,0,0,0.15)" strokeWidth={0.6}
              />
              {filled ? (
                <text
                  x={cell.rx + cell.rw / 2} y={cell.ry + cell.rh / 2 + Math.min(cw, ch) * 0.1}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(255,255,255,0.95)"
                  fontSize={Math.max(5, Math.min(cw, ch) * 0.34)}
                  fontWeight="700" fontFamily="sans-serif"
                >
                  {b.brand_name?.[0]?.toUpperCase()}
                </text>
              ) : (
                cw >= 8 && ch >= 8 && (
                  <text
                    x={cell.rx + cell.rw / 2} y={cell.ry + cell.rh / 2 + Math.min(cw, ch) * 0.1}
                    textAnchor="middle" dominantBaseline="middle"
                    fill="rgba(0,0,0,0.3)"
                    fontSize={Math.max(5, Math.min(cw, ch) * 0.28)}
                    fontWeight="600" fontFamily="sans-serif"
                  >
                    {cell.sub}
                  </text>
                )
              )}
            </g>
          );
        })}
        {w >= CELL && (
          <text
            x={x+w/2} y={y+h-3} textAnchor="middle"
            fill="rgba(0,0,0,0.36)" fontSize={fs} fontWeight="700" fontFamily="sans-serif"
            style={{ pointerEvents: "none" }}
          >
            {slot.label.trim()}
          </text>
        )}
      </g>
    );
  }

  return (
    <g
      style={{ cursor: hasVendor ? "pointer" : "default" }}
      onClick={hasVendor ? onClick : undefined}
      filter={filterId}
      role={hasVendor ? "button" : undefined}
      aria-label={hasVendor ? `${booking?.brand_name}, Slot ${slot.label}` : undefined}
    >
      <rect
        x={x+IN} y={y+IN} width={w-IN*2} height={h-IN*2}
        rx={rx} fill={displayColor}
        stroke={
          isActive    ? "#7F77DD"
          : hasVendor ? "rgba(0,0,0,0.22)"
          :             "rgba(0,0,0,0.09)"
        }
        strokeWidth={isActive ? 2.2 : hasVendor ? 0.9 : 0.6}
      />
      {tex && (
        <rect
          x={x+IN} y={y+IN} width={w-IN*2} height={h-IN*2}
          rx={rx} fill={tex} opacity={hasVendor ? 0.9 : 0.55}
        />
      )}
      {hasVendor && (
        <rect
          x={x+IN} y={y+IN} width={w-IN*2} height={h-IN*2}
          rx={rx} fill="rgba(0,0,0,0.04)"
        />
      )}
      <rect
        x={x+IN+2.5} y={y+IN+2.5} width={w-IN*2-5} height={2.5}
        rx={1.5}
        fill={hasVendor ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.22)"}
      />

      {/* Logo or initial — only if tile is large enough */}
      {hasVendor && w >= CELL * 1.5 && h >= CELL * 1.5 && (
        booking?.logo_url ? (
          <>
            <circle
              cx={x+w/2} cy={y+h/2-2} r={13}
              fill="#fff" stroke="rgba(0,0,0,0.07)" strokeWidth="1"
            />
            <image
              href={booking.logo_url}
              x={x+w/2-10} y={y+h/2-12}
              width={20} height={20}
              style={{ clipPath: "circle(10px at 10px 10px)" }}
            />
          </>
        ) : (
          <text
            x={x+w/2} y={y+h/2+1} textAnchor="middle"
            fill="rgba(255,255,255,0.92)"
            fontSize={Math.min(w, h) * 0.28}
            fontWeight="700" fontFamily="sans-serif"
          >
            {booking?.brand_name?.[0]?.toUpperCase()}
          </text>
        )
      )}

      {/* Slot label */}
      {w >= CELL && (
        <text
          x={x+w/2} y={y+h-5} textAnchor="middle"
          fill={hasVendor ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.32)"}
          fontSize={fs} fontWeight="600" fontFamily="sans-serif"
        >
          {slot.label.trim()}
        </text>
      )}

      {/* Multi-vendor badge */}
      {hasVendor && vendorCount > 1 && w >= CELL * 1.3 && h >= CELL * 1.3 && (
        <g>
          <circle
            cx={x+w-IN-5} cy={y+h-IN-5} r={7}
            fill="#1f2937" stroke="#fff" strokeWidth={1.3}
          />
          <text
            x={x+w-IN-5} y={y+h-IN-5+3} textAnchor="middle"
            fill="#fff" fontSize={7} fontWeight="700" fontFamily="sans-serif"
          >
            +{vendorCount - 1}
          </text>
        </g>
      )}

      {/* Status dot */}
      <circle
        cx={x+w-IN-4} cy={y+IN+4} r={2.8}
        fill={
          isReserved ? "#f590b0"
          : hasVendor ? "#22c55e"
          :             "rgba(0,0,0,0.16)"
        }
        stroke={hasVendor || isReserved ? "rgba(255,255,255,0.5)" : "none"}
        strokeWidth={0.8}
      />
    </g>
  );
}

// ── Legend ─────────────────────────────────────────────────────────────────────
export function MapLegend({ onTypeHover }) {
  return (
    <div className="vmap-legend">
      {Object.entries(TENT_LABELS).map(([type, label]) => (
        <div
          key={type}
          className="vmap-leg-item"
          onMouseEnter={() => onTypeHover?.(type)}
          onMouseLeave={() => onTypeHover?.(null)}
        >
          <span
            className="vmap-leg-dot"
            style={{ background: TENT_COLORS[type] }}
          />
          <span>{label}</span>
        </div>
      ))}
      <div className="vmap-leg-item">
        <span
          className="vmap-leg-dot"
          style={{ background: "#c5dba0", outline: "0.5px solid #9dc070" }}
        />
        <span>Available</span>
      </div>
    </div>
  );
}

// ── VendorMap (main export) ────────────────────────────────────────────────────
// Always renders full-bleed now — there is no fullscreen toggle and no
// vendor-list panel. The map is the only UI: a full-viewport base layer
// (position: absolute, inset: 0, z-index 0 in the CSS) with a small
// floating legend on desktop. Desktop pans via mouse drag and zooms via
// the scroll wheel; mobile keeps its existing touch pan/pinch.
export default function VendorMap({
  // Data
  slots,
  reservedSlots,
  loading,
  error,
  // Interaction
  activeTile,
  onTileClick,
}) {
  const mapWrapRef = useRef(null);

  const {
    isMobile,
    mobilePan,
    mobileZoom,
    onTouchStart,
    onTouchEnd,
    desktopPan,
    desktopZoom,
    isDragging,
    onMouseDown,
    zoomIn,
    zoomOut,
  } = useMapInteraction(mapWrapRef);

  const handleTileClick = useCallback(slotId => {
    onTileClick(slotId);
  }, [onTileClick]);

  // ── SVG ──────────────────────────────────────────────────────────────────────
  const MapSVG = (
    <svg
      viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
      width="100%" height="100%"
      style={{ display: "block" }}
      aria-label="Festival vendor map"
      preserveAspectRatio="xMidYMid slice"
    >
      <MapDefs/>
      <rect x={VB_X} y={VB_Y} width={VB_W} height={VB_H} fill="url(#pat-grass)"/>

      {/* Pathways render below tiles */}
      {slotsConfig
        .filter(s => s.type === "pathway")
        .map(slot => <SvgPathway key={`pw-${slot.id}`} slot={slot}/>)}

      {/* Vendor tiles on top */}
      {slotsConfig
        .filter(s => s.type !== "pathway")
        .map(slot => (
          <MapTile
            key={slot.id}
            slot={slot}
            bookings={slots[slot.id] || []}
            isReserved={reservedSlots.includes(slot.id)}
            isActive={activeTile === slot.id}
            onClick={() => handleTileClick(slot.id)}
          />
        ))}
    </svg>
  );

  return (
    <div className="vmap-root">

      {/* ── Map area — the full-bleed base layer ────────────────────────── */}
      <div className="vmap-map-col">
        <div
          className={`vmap-map-inner ${
            !isMobile ? (isDragging ? "vmap-map-inner--dragging" : "vmap-map-inner--pannable") : ""
          }`}
          ref={mapWrapRef}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onMouseDown={!isMobile ? onMouseDown : undefined}
        >
          {/* Loading overlay */}
          {loading && (
            <div className="vmap-loading">
              <div className="vmap-spinner"/>
              <div className="vmap-loading-text">Loading map…</div>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="vmap-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/*
            Mobile's transform math (panToSlot in useMapInteraction) assumes
            this element is sized in VB_W x VB_H units, hence the explicit
            pixel width/height + "center center" origin below. Desktop uses
            plain screen-pixel drag/zoom against the element's natural
            100% x 100% size, with "0 0" origin so the wheel-zoom math in
            the hook (which projects from/to that origin) lines up.
          */}
          <div
            className="vmap-transform"
            style={
              isMobile
                ? {
                    transform: `translate(${mobilePan.x}px,${mobilePan.y}px) scale(${mobileZoom})`,
                    transformOrigin: "center center",
                    width: `${VB_W}px`,
                    height: `${VB_H}px`,
                  }
                : {
                    transform: `translate(${desktopPan.x}px,${desktopPan.y}px) scale(${desktopZoom})`,
                    transformOrigin: "0 0",
                  }
            }
          >
            {MapSVG}
          </div>
        </div>
      </div>

      {/* ── Floating legend — desktop only, small card over the map ──────── */}
      {!isMobile && (
        <div className="vmap-floating-legend">
          <MapLegend/>
        </div>
      )}

      {/* ── Zoom control — desktop only, top-right ───────────────────────── */}
      {!isMobile && (
        <div className="vmap-zoom-ctrl" role="group" aria-label="Zoom controls">
          <button
            type="button"
            className="vmap-zoom-btn"
            onClick={zoomIn}
            disabled={desktopZoom >= 4}
            aria-label="Zoom in"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
          <div className="vmap-zoom-ctrl-divider" aria-hidden="true"/>
          <button
            type="button"
            className="vmap-zoom-btn"
            onClick={zoomOut}
            disabled={desktopZoom <= 1}
            aria-label="Zoom out"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M5 12h14"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}